import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Barbell } from "@phosphor-icons/react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "coach" ? "/coach" : "/app", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#0A0A0A]">
      {/* Left visual */}
      <div className="hidden md:block relative overflow-hidden border-r border-[#262626]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/14898419/pexels-photo-14898419.jpeg)' }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 id-stripes" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={24} color="#fff" />
            </div>
            <span className="font-oswald text-2xl font-bold uppercase tracking-widest">Lazy Lean Method<sup className="font-manrope text-xs font-semibold tracking-normal ml-1 -top-2 relative">™</sup></span>
          </div>
          <div>
            <div className="id-label text-zinc-300 mb-3">Online Coaching · For Men</div>
            <h1 className="id-h1 text-white max-w-md">
              For men<br/>
              who <span className="text-[#FF3333]">hate diets.</span>
            </h1>
            <p className="text-zinc-300 max-w-md mt-6 font-manrope text-lg">
              Lose your first <span className="text-white font-bold">10 kg</span> with the <span className="text-white font-bold">7 Lazy Lean Habits™</span>.
            </p>
            <p className="text-zinc-500 max-w-md mt-3 font-manrope text-sm uppercase tracking-[0.2em]">
              No calorie counting required.
            </p>
          </div>
          <div className="id-label text-zinc-500">— The Lazy Lean Method™ —</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={18} color="#fff" />
            </div>
            <span className="font-oswald text-xl font-bold uppercase tracking-widest">Lazy Lean Method<sup className="font-manrope text-[11px] font-semibold tracking-normal ml-0.5 -top-1.5 relative">™</sup></span>
          </div>

          <div className="id-label mb-3">— Member Access —</div>
          <h2 className="id-h2 mb-2">Sign in</h2>
          <p className="text-zinc-400 mb-8 font-manrope text-sm">Welcome back. Let's keep the habits running.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="id-label block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="id-input"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="id-label block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="id-input"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            {err && (
              <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] font-manrope" data-testid="login-error">
                {err}
              </div>
            )}

            <button type="submit" disabled={loading} className="id-btn-primary w-full" data-testid="login-submit-button">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#262626]">
            <p className="text-sm text-zinc-400 font-manrope">
              New client?{" "}
              <Link to="/register" data-testid="link-to-register" className="text-[#FF3333] hover:text-[#CC0000] font-semibold uppercase tracking-wider text-xs">
                Create an account →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
